// Kampf am Ufer (§6): Echtzeit mit Pause (K46, Fabi mit dem Projekt-Lead, 25.10.2026; Fabi 26.10.2026). Befehle an die
// Freunde, Kniffe auf Befehl mit eigenem Ziel, die neuen Kniffe des Übungsufers (K50 bis K52) und die Ansagen der
// Sturmwesen (K48). Befehle sind Eingaben mit dem Takt, ab dem sie gelten; so läuft derselbe Kampf mit denselben
// Befehlen immer gleich ab.
import { getAbility } from '../../content/index.ts';
import type { BattleCommand, Order, TelegraphShape, Unit } from '../types.ts';
import type { Battle } from './core.ts';
import {
  EPS,
  clampToField,
  dealDamage,
  dist,
  emit,
  enemiesOf,
  gap,
  heal,
  hitDamage,
  mostHurtFriend,
  moveToward,
  nearest,
  optParam,
  param,
  round,
  setSpotOpenHandler,
  setStatus,
  shove,
  targetable,
  unitById,
} from './core.ts';
import { fellHit, fellShape, guard, hindmostFoe, mostHpEnemy, secondRowFoe, startRaidOn, useKniff } from './kniffe.ts';
import { clearShot, kniffArt, launch } from './shots.ts';
import { freePoint, inBush } from './terrain.ts';

// ---------- Befehle ----------

/** K46: Befehle, deren Takt gekommen ist, gelten ab jetzt; ein neuer Befehl ersetzt den alten sofort */
export function applyCommands(b: Battle): void {
  if (!b.commands.length) return;
  const n = Math.round(b.t / b.rules.tickSeconds);
  while (b.commands.length && b.commands[0]!.tick <= n) giveOrder(b, b.commands.shift()!);
}

function setOrder(b: Battle, u: Unit, order: Order | null): void {
  u.order = order;
  emit(b, { t: round(b.t), type: 'order', unit: u.uid, order: order ? { ...order } : null });
}

export function giveOrder(b: Battle, c: BattleCommand): void {
  const u = b.units[c.unit];
  if (!u || !u.alive || u.side !== 'friend' || u.kind === 'tree') return;
  const f = b.rules.field;
  switch (c.kind) {
    case 'clear':
      if (u.order) setOrder(b, u, null);
      return;
    case 'move': {
      const p = freePoint(b.setup.obstacles ?? [], { x: Math.min(f.width - u.radius, Math.max(u.radius, c.x)), y: Math.min(f.height - u.radius, Math.max(u.radius, c.y)) }, u.radius);
      // Wer woanders hingeht, lässt sein Ziel los; danach sucht er sich wieder selbst eins
      u.targetUid = null;
      u.engagedUid = null;
      setOrder(b, u, { kind: 'move', x: round(p.x), y: round(p.y) });
      return;
    }
    case 'attack': {
      const t = b.units[c.target];
      if (!t || !t.alive || t.side === u.side || t.kind === 'tree') return;
      setOrder(b, u, { kind: 'attack', target: t.uid });
      return;
    }
    case 'kniff': {
      if (!u.kniff) return;
      setOrder(b, u, { kind: 'kniff', ...(c.x !== undefined ? { x: c.x } : {}), ...(c.y !== undefined ? { y: c.y } : {}), ...(c.target !== undefined ? { target: c.target } : {}) });
      return;
    }
  }
}

/** K46: Hält der Befehl ihn in diesem Takt beschäftigt? Erledigte Befehle fallen weg, dann handelt er wieder selbst */
export function updateOrder(b: Battle, u: Unit, fire: (b: Battle, u: Unit) => boolean): boolean {
  const o = u.order!;
  if (o.kind === 'move') {
    const d = Math.hypot(o.x - u.x, o.y - u.y);
    if (d <= 0.6) {
      setOrder(b, u, null);
      return false;
    }
    // Kommt er nicht weiter (eingekeilt), gibt er nach zwei Sekunden auf [ZAHL?]
    if (o.best === undefined || d < o.best - 0.5) {
      o.best = d;
      o.since = b.t;
    } else if (b.t - (o.since ?? b.t) > 2) {
      setOrder(b, u, null);
      return false;
    }
    u.targetUid = null;
    moveToward(b, u, o.x, o.y);
    return true;
  }
  if (o.kind === 'attack') {
    const t = unitById(b, o.target);
    if (!t || !t.alive) {
      setOrder(b, u, null);
      return false;
    }
    return false; // angreifen wie sonst, nur auf dieses Ziel (orderTarget)
  }
  // Kniff: zündet, sobald er frei ist und der Mut voll; ohne vollen Mut verfällt der Befehl
  if (!u.kniff || u.mut < u.mutMax - EPS) {
    setOrder(b, u, null);
    return false;
  }
  const ok = fire(b, u);
  if (u.order === o) setOrder(b, u, null);
  return ok;
}

/** K46: das befohlene Ziel; undefined ohne Angriffsbefehl, null, wenn er gerade warten muss (eingegraben, eingerollt) */
export function orderTarget(b: Battle, u: Unit): Unit | null | undefined {
  if (u.order?.kind !== 'attack') return undefined;
  const t = unitById(b, u.order.target);
  if (!t || !t.alive) return undefined;
  return targetable(b, t) ? t : null;
}

/** K49: Fernkämpfer zielen nicht ins Gebüsch und nehmen lieber jemanden mit freier Bahn */
export function preferClear(b: Battle, u: Unit, tgt: Unit | undefined): Unit | undefined {
  if (!tgt || !b.setup.obstacles?.length || u.range < 10 || u.attackHeal > 0) return tgt;
  if (clearShot(b, u, tgt)) return tgt;
  const seen = enemiesOf(b, u).filter((o) => o.kind !== 'tree' && !inBush(b.setup.obstacles, o));
  const free = seen.filter((o) => gap(u, o) <= u.range + EPS && clearShot(b, u, o));
  if (free.length) return nearest(u, free);
  // Hinter einem Stein schießt er trotzdem (das Geschoss bleibt hängen); wer im Gebüsch steht, den sieht er nicht
  return seen.includes(tgt) ? tgt : nearest(u, seen);
}

// ---------- Die neuen Kniffe (K50, K51) ----------

/** Wohin ein Kniff geht: aus dem Befehl oder, ohne Befehl (Harness „ohne Eingreifen“), wie früher */
type Aim = { x?: number; y?: number; target?: number };
const aimOf = (u: Unit): Aim | null => (u.order?.kind === 'kniff' ? u.order : null);

/** Einen Punkt höchstens reach vom Werfer entfernt und frei von Hindernissen */
function within(b: Battle, u: Unit, p: { x: number; y: number }, reach: number): { x: number; y: number } {
  const d = Math.hypot(p.x - u.x, p.y - u.y);
  const k = d > reach ? reach / d : 1;
  const f = b.rules.field;
  const q = { x: Math.min(f.width - u.radius, Math.max(u.radius, u.x + (p.x - u.x) * k)), y: Math.min(f.height - u.radius, Math.max(u.radius, u.y + (p.y - u.y) * k)) };
  return freePoint(b.setup.obstacles ?? [], q, u.radius);
}

/** K50 Bau graben (neu): unter der Erde zum Ort, dort graben; startet nur, gibt den Ort zurück */
export function startMulde(b: Battle, u: Unit, k: string): { x: number; y: number } {
  const aim = aimOf(u);
  const p = within(b, u, aim?.x !== undefined && aim.y !== undefined ? { x: aim.x, y: aim.y } : u, param(u, k, 'reach'));
  // Erst gräbt er sich ein (eingraben), dann wühlt er unter der Erde hin, taucht auf und gräbt
  u.travel = { kind: 'tunnel', x: p.x, y: p.y, speed: param(u, k, 'tunnelSpeed'), safe: true, by: null, until: b.t + 5, startAt: b.t + param(u, k, 'burrowSeconds') };
  return p;
}

/** K50 Stachelball (neu): rollt zum Ort und kreiselt dort */
export function startBall(b: Battle, u: Unit, k: string): { x: number; y: number } | null {
  const aim = aimOf(u);
  const auto = mostHpEnemy(b, u);
  const goal = aim?.x !== undefined && aim.y !== undefined ? { x: aim.x, y: aim.y } : auto;
  if (!goal) return null;
  const p = within(b, u, goal, param(u, k, 'reach'));
  u.travel = { kind: 'roll', x: p.x, y: p.y, speed: u.moveSpeed * param(u, k, 'rushSpeed'), safe: false, by: null, until: b.t + 3 };
  setStatus(b, u, 'spinning', b.t);
  return p;
}

/** K50 Wunden auswaschen (neu): winkt einen Freund heran; ohne Wahl den, dem es am schlechtesten geht */
export function startBeckon(b: Battle, u: Unit, k: string): Unit | null {
  const aim = aimOf(u);
  const chosen = aim?.target !== undefined ? unitById(b, aim.target) : mostHurtFriend(b, u, true);
  if (!chosen || !chosen.alive || chosen.side !== u.side || chosen.kind === 'tree') return null;
  if (chosen === u) {
    wash(b, u, u);
    return u;
  }
  chosen.travel = { kind: 'beckon', x: u.x, y: u.y, speed: param(u, k, 'dashSpeed'), safe: true, by: u.uid, until: b.t + param(u, k, 'maxSeconds') };
  // Was er gerade tut, bricht ab: Der Waschbär holt ihn aus dem Pulk
  chosen.spin = null;
  chosen.leap = null;
  chosen.guardRun = null;
  chosen.march = null;
  chosen.targetUid = null;
  chosen.engagedUid = null;
  if (chosen.order) setOrder(b, chosen, null);
  return chosen;
}

/** Der Waschbär wäscht ihn aus: das Waschwasser fliegt hinüber, Gründlich wirkt wie immer */
function wash(b: Battle, by: Unit, f: Unit): void {
  const k = by.kniff!;
  const via = `kniff:${k}`;
  const art = b.setup.projectiles ? kniffArt(b, k) : null;
  if (art && f !== by) launch(b, by, f, { kind: 'heal', amount: param(by, k, 'heal'), via }, art);
  else heal(b, by, f, param(by, k, 'heal'), via);
}

/** K51 Schwachstelle (Kniff): die Zielscheibe auf ein Sturmwesen; ohne Wahl ein zufälliges, wie früher */
export function markWeakSpot(b: Battle, u: Unit): Unit | null {
  const aim = aimOf(u);
  const foes = b.units.filter((o) => o.side !== u.side && o.alive && o.kind !== 'tree');
  const chosen = aim?.target !== undefined ? unitById(b, aim.target) : foes[Math.floor(b.rng.next() * foes.length)];
  if (!chosen || !chosen.alive || chosen.side === u.side) return null;
  chosen.marked = true;
  chosen.markedBy = u.uid;
  chosen.markHold = true;
  chosen.markClaim = null;
  chosen.markReservedBy = null;
  emit(b, { t: round(b.t), type: 'mark', by: u.uid, to: chosen.uid });
  return chosen;
}

// ---------- Unterwegs, Graben, Schlittern, Sturzflug ----------

/** K46: unterwegs auf eigene Art; true, solange er unterwegs ist */
export function updateTravel(b: Battle, u: Unit): boolean {
  const tr = u.travel!;
  const step = tr.speed * b.rules.tickSeconds;
  if (tr.kind === 'beckon') {
    const by = unitById(b, tr.by);
    if (!by || !by.alive) {
      u.travel = null;
      clampToField(b, u);
      return false;
    }
    const d = Math.max(EPS, dist(u, by));
    const want = u.radius + by.radius + 0.6;
    const goal = { x: by.x + ((u.x - by.x) / d) * want, y: by.y + ((u.y - by.y) / d) * want };
    const left = Math.hypot(goal.x - u.x, goal.y - u.y);
    if (left <= step || b.t + EPS >= tr.until) {
      if (left <= step) {
        u.x = goal.x;
        u.y = goal.y;
      }
      u.travel = null;
      clampToField(b, u);
      const before = b.via;
      b.via = `kniff:${by.kniff}`;
      try {
        wash(b, by, u);
      } finally {
        b.via = before;
      }
      return true;
    }
    u.x += ((goal.x - u.x) / left) * step;
    u.y += ((goal.y - u.y) / left) * step;
    return true;
  }
  const left = Math.hypot(tr.x - u.x, tr.y - u.y);
  if (tr.kind === 'tunnel') {
    if (tr.startAt !== undefined && b.t + EPS < tr.startAt) return true;
    if (left <= step || b.t + EPS >= tr.until) {
      if (left <= step) {
        u.x = tr.x;
        u.y = tr.y;
      }
      u.travel = null;
      clampToField(b, u);
      // auftauchen, dann graben; erst danach sackt der Boden ab
      u.digUntil = b.t + param(u, u.kniff!, 'surfaceSeconds') + param(u, u.kniff!, 'digSeconds');
      return true;
    }
    u.x += ((tr.x - u.x) / left) * step;
    u.y += ((tr.y - u.y) / left) * step;
    return true;
  }
  // roll: am Ort angekommen, in den Pulk gerollt oder zu lange unterwegs, dann kreiseln
  const bumped = left < 8 && b.units.some((o) => o.side !== u.side && o.alive && o.kind !== 'tree' && gap(u, o) <= 0.3);
  if (left <= 1.2 || bumped || b.t + EPS >= tr.until) {
    u.travel = null;
    const k = u.kniff!;
    u.spin = { targetUid: -1, started: b.t, until: b.t + param(u, k, 'duration'), nextTick: b.t, point: true };
    return true;
  }
  moveToward(b, u, tr.x, tr.y, tr.speed / Math.max(EPS, u.moveSpeed));
  return true;
}

/** K50: Er gräbt; ist er fertig, sackt der Boden ab */
export function updateDig(b: Battle, u: Unit): boolean {
  if (b.t + EPS < u.digUntil) return true;
  u.digUntil = 0;
  const k = u.kniff!;
  const before = b.via;
  b.via = `kniff:${k}`;
  try {
    collapse(b, u, k);
  } finally {
    b.via = before;
  }
  return true;
}

/** K50: Sturmwesen im Umkreis schlittern zum Dachs in die Mulde und stecken fest; er und seine Nachbarn sind geschützt */
function collapse(b: Battle, u: Unit, k: string): void {
  const slideSeconds = param(u, k, 'slideSeconds');
  const stuck = param(u, k, 'stuckSeconds');
  for (const o of b.units) {
    if (o.side === u.side || !o.alive || o.kind === 'tree' || o.travel?.safe || gap(u, o) > param(u, k, 'radius') + EPS) continue;
    const d = Math.max(EPS, dist(u, o));
    const want = u.radius + o.radius + 0.4;
    const to = { x: u.x + ((o.x - u.x) / d) * want, y: u.y + ((o.y - u.y) / d) * want };
    o.slide = { x: to.x, y: to.y, until: b.t + slideSeconds, speed: Math.max(1, Math.hypot(to.x - o.x, to.y - o.y) / slideSeconds) };
    o.stuckUntil = b.t + slideSeconds + stuck;
    // Wer schlittert, bringt seinen angesagten Angriff nicht an und springt nicht weg
    if (o.windup) {
      emit(b, { t: round(b.t), type: 'telegraphEnd', id: o.windup.id, unit: o.uid, done: false });
      o.windup = null;
    }
    o.raid = null;
    o.leap = null;
    o.pounce = null;
    setStatus(b, o, 'sliding', b.t + slideSeconds);
    setStatus(b, o, 'stuck', o.stuckUntil);
  }
  const until = b.t + param(u, k, 'duration');
  for (const o of b.units) {
    if (o.side !== u.side || !o.alive || o.kind === 'tree' || (o !== u && dist(o, u) > param(u, k, 'guardRadius') + EPS)) continue;
    guard(b, u, o, param(u, k, 'damageReduction'), until);
  }
}

/** K50: schlittert in die Mulde, kann dabei nichts tun */
export function updateSlide(b: Battle, u: Unit): boolean {
  const s = u.slide!;
  const d = Math.hypot(s.x - u.x, s.y - u.y);
  const step = s.speed * b.rules.tickSeconds;
  if (d <= step || b.t + EPS >= s.until) {
    if (d <= step) {
      u.x = s.x;
      u.y = s.y;
    }
    u.slide = null;
    clampToField(b, u);
    return true;
  }
  u.x += ((s.x - u.x) / d) * step;
  u.y += ((s.y - u.y) / d) * step;
  return true;
}

/** K52 Sturzflug: Trifft ein Kniff eine Schwachstelle, stürzt der Eisvogel, der sie ausgemacht hat, sofort hinterher */
setSpotOpenHandler((b, tgt) => {
  const bird = unitById(b, tgt.markedBy);
  if (!bird || !bird.alive || bird.passive !== 'sturzflug-folge' || bird.swoop || bird.travel) return;
  const at = b.t + param(bird, 'sturzflug-folge', 'windup');
  bird.swoop = { target: tgt.uid, at, back: at + param(bird, 'sturzflug-folge', 'back'), hit: false };
  emit(b, { t: round(b.t), type: 'swoop', unit: bird.uid, target: tgt.uid, at: round(at) });
});

export function updateSwoop(b: Battle, u: Unit): boolean {
  const s = u.swoop!;
  if (!s.hit && b.t + EPS >= s.at) {
    s.hit = true;
    const v = unitById(b, s.target);
    if (v && v.alive) {
      const raw = hitDamage(b, u, param(u, 'sturzflug-folge', 'damageFactor')) + v.maxHp * param(u, 'sturzflug-folge', 'maxHpShare');
      dealDamage(b, u, v, raw, 'kniff', 'passive:sturzflug-folge', { crit: true, noMark: true });
    }
  }
  if (b.t + EPS >= s.back) {
    u.swoop = null;
    return false;
  }
  return true;
}

// ---------- Ansagen der Sturmwesen (K48) ----------

/** Hat dieser Kniff eine Ansage? */
export const telegraphed = (b: Battle, u: Unit, k: string): boolean =>
  // S13 (Erste Fassung): auch der Baum des Bibers wird angesagt, damit man sieht, wohin er fällt
  !!b.setup.telegraphs && (u.side === 'enemy' || getAbility(k).mechanic === 'fell') && optParam(u, k, 'telegraph') > 0;

/**
 * K48 (Fabi, 26.10.2026): Alles, was wehtut, wird vorher am Boden angezeigt. Gibt false zurück, wenn es gerade kein Ziel
 * gibt; ownKniff: false beim ersten Sprung des Windschnitts, der kein Kniff ist
 */
export function startWindup(b: Battle, u: Unit, k: string, ownKniff = true): boolean {
  const mech = getAbility(k).mechanic;
  let shape: TelegraphShape;
  let target: Unit | undefined;
  const current = unitById(b, u.targetUid);
  const foe = current && current.alive && current.side !== u.side && targetable(b, current) ? current : nearest(u, enemiesOf(b, u).filter((o) => o.kind !== 'tree'));
  switch (mech) {
    case 'ram': {
      target = foe;
      const len = param(u, k, 'stripeLength');
      if (!target || gap(u, target) > len - u.radius) return false;
      const d = Math.max(EPS, dist(u, target));
      shape = { kind: 'stripe', x: round(u.x), y: round(u.y), dx: round((target.x - u.x) / d), dy: round((target.y - u.y) / d), length: len + u.radius, width: param(u, k, 'stripeWidth') };
      break;
    }
    case 'fell': {
      // S13 Baum fällen: ein Schatten am Boden zeigt den Streifen, in den der Stamm fällt
      target = foe;
      const stripe = target ? fellShape(u, k, target) : null;
      if (!stripe) return false;
      shape = stripe;
      break;
    }
    case 'raid':
      target = u.behavior === 'skirmisher' ? secondRowFoe(b, u) : hindmostFoe(b, u);
      if (!target) return false;
      shape = { kind: 'mark', target: target.uid };
      break;
    case 'strike':
      target = foe;
      if (!target || gap(u, target) > u.range + EPS) return false;
      shape = { kind: 'mark', target: target.uid };
      break;
    case 'haste':
      shape = { kind: 'ring', radius: u.radius + 3 };
      break;
    // K53 (Erste Fassung, Fabi: alle großen Angriffe am Boden ansagen): Grollen, Auf die Augen, Blattschwert
    case 'rumble':
      // Grollen trifft alle im Umkreis: der Ring zeigt genau diesen Umkreis
      if (!enemiesOf(b, u).some((o) => o.kind !== 'tree' && dist(o, u) <= param(u, k, 'radius') + EPS)) return false;
      shape = { kind: 'ring', radius: param(u, k, 'radius') };
      break;
    case 'blind': {
      // Auf die Augen: Dreck auf das nächste Tier und alle drumherum
      target = nearest(u, enemiesOf(b, u).filter((o) => o.kind !== 'tree'));
      if (!target || gap(u, target) > u.range * 1.5) return false;
      shape = { kind: 'area', target: target.uid, radius: param(u, k, 'radius') };
      break;
    }
    case 'pounce': {
      // Blattschwert: springt zu dem Tier in seiner Nähe, das am meisten Schaden gemacht hat
      const near = b.units.filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree' && dist(o, u) <= param(u, k, 'radius') + EPS);
      target = [...near].sort((a, c) => c.stats.damage - a.stats.damage || dist(a, u) - dist(c, u) || a.uid - c.uid)[0] ?? foe;
      if (!target) return false;
      shape = { kind: 'mark', target: target.uid };
      break;
    }
    default:
      return false;
  }
  if (ownKniff) u.mut = 0;
  const id = b.nextTelegraphId++;
  const at = b.t + param(u, k, 'telegraph');
  u.windup = { id, kniff: k, at, shape, target: target?.uid ?? null };
  emit(b, { t: round(b.t), type: 'telegraph', id, unit: u.uid, kniff: k, at: round(at), shape });
  return true;
}

/** K48: Wer ausholt, steht still (das Hagelchen wirft weiter); dann kommt der Angriff */
export function updateWindup(b: Battle, u: Unit): boolean {
  const w = u.windup!;
  if (b.t + EPS < w.at) return w.shape.kind !== 'ring';
  u.windup = null;
  const before = b.via;
  b.via = `kniff:${w.kniff}`;
  let done = false;
  try {
    done = strikeAfterWindup(b, u, w);
  } finally {
    b.via = before;
  }
  emit(b, { t: round(b.t), type: 'telegraphEnd', id: w.id, unit: u.uid, done });
  return true;
}

function strikeAfterWindup(b: Battle, u: Unit, w: NonNullable<Unit['windup']>): boolean {
  const k = w.kniff;
  const own = k === u.kniff;
  const done = (target: number | null) => {
    if (own) u.kniffUses += 1;
    emit(b, { t: round(b.t), type: 'kniff', unit: u.uid, kniff: k, target });
    return true;
  };
  // K53 (Erste Fassung): Grollen, Auf die Augen und Blattschwert laufen nach der Ansage wie immer; der Mut ist schon verbraucht
  if (own && ['rumble', 'blind', 'pounce'].includes(getAbility(k).mechanic ?? '')) return useKniff(b, u);
  const shape = w.shape;
  if (shape.kind === 'stripe' && getAbility(k).mechanic === 'fell') {
    // S13: Der Stamm kracht, der Biber bleibt stehen
    fellHit(b, u, k, shape);
    return done(null);
  }
  if (shape.kind === 'stripe') {
    // Rammstoß: Er stürmt ein Stück vor; wer im Streifen steht, wird getroffen und geschubst
    const hit: Unit[] = [];
    for (const o of b.units) {
      if (o.side === u.side || !o.alive || o.kind === 'tree' || !targetable(b, o)) continue;
      const rx = o.x - shape.x;
      const ry = o.y - shape.y;
      const along = rx * shape.dx + ry * shape.dy;
      const across = Math.abs(rx * shape.dy - ry * shape.dx);
      if (along >= -o.radius && along <= shape.length + o.radius && across <= shape.width / 2 + o.radius) hit.push(o);
    }
    u.x += shape.dx * param(u, k, 'charge');
    u.y += shape.dy * param(u, k, 'charge');
    clampToField(b, u);
    emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'charge' });
    for (const o of hit) {
      dealDamage(b, u, o, hitDamage(b, u, param(u, k, 'damageFactor')), 'kniff');
      if (!o.alive) continue;
      const push = param(u, k, 'knockback') * (b.rules.edge.pushRadius / o.radius) ** 2;
      shove(b, o, shape.dx * push, shape.dy * push);
      if (o.alive) emit(b, { t: round(b.t), type: 'move', unit: o.uid, x: round(o.x), y: round(o.y), reason: 'knockback' });
    }
    return done(hit[0]?.uid ?? null);
  }
  if (shape.kind === 'ring') {
    // Hagelschauer: eine Weile viel schnellere Würfe
    u.hasteUntil = b.t + param(u, k, 'duration');
    u.hasteFactor = param(u, k, 'attackSpeedFactor');
    setStatus(b, u, 'hasted', u.hasteUntil);
    return done(null);
  }
  const t = unitById(b, w.target);
  if (getAbility(k).mechanic === 'raid') {
    // Sprung des Windschnitts: zu dem Freund, den die Marke gezeigt hat; ist der nicht mehr da, zum nächsten hintersten
    const to = t && targetable(b, t) ? t : hindmostFoe(b, u);
    if (!to) return false;
    startRaidOn(b, u, to);
    return own ? done(to.uid) : true;
  }
  // Messerhieb: nur, wenn der Freund noch vor ihm steht
  if (!t || !targetable(b, t) || gap(u, t) > u.range + 1.5) return false;
  dealDamage(b, u, t, hitDamage(b, u, param(u, k, 'damageFactor')) + t.maxHp * optParam(u, k, 'maxHpShare'), 'kniff');
  return done(t.uid);
}

/** K46: Kniff bereit (für das Bild) */
export const ready = (u: Unit): boolean => !!u.kniff && u.mut >= u.mutMax - EPS;

/** Der Ort, auf den ein Kniff mit Ortsziel gehen würde (für Tests und Vorschau) */
export const kniffReach = (u: Unit): number => (u.kniff ? optParam(u, u.kniff, 'reach') : 0);
